import { defineStore } from 'pinia';
import { ref } from 'vue';
type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'text' | 'danger' | 'icon' | 'menu-item';
type ButtonSize = 'default' | 'small' | 'medium' | 'large' | 'extra-small' | number;
interface ButtonProps {
    label?: string;
    variant?: ButtonVariant | ButtonVariant[];
    size?: ButtonSize;
    disabled?: boolean;
    type?: 'button' | 'submit' | 'reset';
    rounded?: boolean;
    highlightOnHover?: boolean;
    border?: boolean;
    action?: any;
    readOnly?: boolean;
    actived?: boolean;
    loading?: boolean;
}
type SnackbarType = 'success' | 'error' | 'info' | 'warning' | 'interactive';
type NotificationType = {
    id: string;
    icon?: string;
    message: string;
    type?: SnackbarType;
    actions?: (ButtonProps & { btnIcon?: string })[];
};
import { getErrorMessage } from '@arbocollab/arbo-common-utils';

// Example API error response

const notificationMessage: Record<string, { success?: string; error?: string }> = {};
export const useNotificationStore = defineStore('toast', () => {
    const showErrorNetMessage = (errorResponse: any) => {
        console.log('Error Response:', errorResponse);
        if (errorResponse?.code === 'offline') {
            addNotification({
                id: 'offline',
                type: 'error',
                message: 'You are currently offline. Please check your network connection.'
            });
        } else {
            const errorMessage = getErrorMessage(errorResponse);
            addNotification({ id: errorResponse?.code, type: 'error', message: errorMessage });
        }
    };
    const notifications = ref<NotificationType[]>([]);
    const showMessage = (code: string, type: 'success' | 'error') => {
        const noti = notificationMessage[code];
        if (noti) {
            const message = noti[type];
            if (message) {
                addNotification({ id: code, type, message });
                return;
            }
        } else {
            addNotification({ id: code, type, message: code });
        }
    };

    // Track timeouts and remaining times for pause/resume
    const timeoutMap = new Map<string | number, { timeoutId: number; remainingTime: number; startTime: number }>();

    /**
     * Adds a new notification and sets a timer to automatically remove it.
     * @param noti - The notification object to add.
     * @param duration - Time in ms before auto-removal. Set to 0 for no timeout. Defaults to 5000ms.
     */
    const addNotification = (noti: NotificationType, duration = 5000) => {
        // Ensure each notification has a unique ID if it doesn't already
        const newNotification = {
            ...noti,
            id: noti.id ?? Date.now() + Math.random()
        };

        notifications.value.push(newNotification);

        if (duration > 0) {
            const startTime = Date.now();
            const timeoutId = setTimeout(() => {
                removeNotification(newNotification.id);
                timeoutMap.delete(newNotification.id);
            }, duration);

            timeoutMap.set(newNotification.id, {
                timeoutId: timeoutId as any,
                remainingTime: duration,
                startTime
            });
        }
    };

    /**
     * Removes a notification by its ID.
     * @param id - The ID of the notification to remove.
     */
    function removeNotification(id: number | string) {
        notifications.value = notifications.value.filter((n) => n.id !== id);
        const entry = timeoutMap.get(id);
        if (entry) {
            clearTimeout(entry.timeoutId);
            timeoutMap.delete(id);
        }
    }

    /**
     * Pauses the auto-dismiss timer for a notification.
     * @param id - The ID of the notification.
     */
    function pauseAutoDismiss(id: number | string) {
        const entry = timeoutMap.get(id);
        if (entry) {
            clearTimeout(entry.timeoutId);
            const elapsed = Date.now() - entry.startTime;
            entry.remainingTime = Math.max(0, entry.remainingTime - elapsed);
        }
    }

    /**
     * Resumes the auto-dismiss timer for a notification.
     * @param id - The ID of the notification.
     */
    function resumeAutoDismiss(id: number | string) {
        const entry = timeoutMap.get(id);
        if (entry && entry.remainingTime > 0) {
            const timeoutId = setTimeout(() => {
                removeNotification(id);
                timeoutMap.delete(id);
            }, entry.remainingTime);
            entry.timeoutId = timeoutId as any;
            entry.startTime = Date.now();
        }
    }

    return {
        notifications,
        addNotification,
        removeNotification,
        showMessage,
        showErrorNetMessage,
        pauseAutoDismiss,
        resumeAutoDismiss
    };
});
